// pages/AnalyticsDetails.jsx
import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Icon from "../../components/AppIcon";
import { analyticsAPI } from "services/api";
import { formatCurrency } from "utils/localization";

const AnalyticsDetails = () => {
  const { type } = useParams(); // 'detailed', 'trends', 'categories', 'activity'
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("last30days");
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        let response;

        switch (type) {
          case "detailed":
            response = await analyticsAPI.getDetailed(timeRange);
            setData(response.data.detailedAnalytics);
            break;
          case "trends":
            response = await analyticsAPI.getMonthlyTrends(timeRange);
            // Transform the data to match what the UI expects
            const trendsData = {
              title: "Spending Trends",
              yearlyData: response.data.monthlyTrends,
            };
            setData(trendsData);
            break;
          case "categories":
            response = await analyticsAPI.getCategoryBreakdown(timeRange);
            // Transform the data to match what the UI expects
            const categoriesData = {
              title: "Category Analysis",
              categories: response.data.categoryBreakdown.map((cat) => ({
                name: cat.category,
                amount: cat.amount,
                percentage: cat.percentage,
                color: cat.color,
                trend:
                  cat.percentage > 30
                    ? "+15%"
                    : cat.percentage > 20
                    ? "+8%"
                    : "-5%", // Placeholder trend
                topEvents: ["Summer Vacation", "Birthday Party", "Team Lunch"], // Placeholder
                avgPerEvent: cat.amount / 5, // Placeholder
              })),
            };
            setData(categoriesData);
            break;
          case "activity":
            response = await analyticsAPI.getRecentActivity(timeRange);
            // Transform the data to match what the UI expects
            const activityData = {
              title: "Activity Timeline",
              activities: response.data.recentActivity,
            };
            setData(activityData);
            break;
          default:
            response = await analyticsAPI.getDetailed(timeRange);
            setData(response.data.detailedAnalytics);
        }
      } catch (err) {
        console.error("Error fetching analytics details:", err);
        setError("Failed to load analytics details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [type, timeRange]);

  const getActivityIcon = (type) => {
    const icons = {
      expense_added: "Plus",
      settlement: "CheckCircle",
      event_created: "Calendar",
      participant_added: "UserPlus",
    };
    return icons[type] || "Activity";
  };

  const getActivityColor = (type) => {
    const colors = {
      expense_added: "text-blue-600 bg-blue-100",
      settlement: "text-green-600 bg-green-100",
      event_created: "text-purple-600 bg-purple-100",
      participant_added: "text-orange-600 bg-orange-100",
    };
    return colors[type] || "text-gray-600 bg-gray-100";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <Icon
            name="AlertCircle"
            size={48}
            className="mx-auto text-red-500 mb-4"
          />
          <h3 className="text-lg font-medium text-red-800 mb-2">
            Error Loading Analytics
          </h3>
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <Icon
          name="AlertCircle"
          size={48}
          className="mx-auto text-red-500 mb-4"
        />
        <h3 className="text-lg font-medium text-foreground mb-2">
          Analytics Not Found
        </h3>
        <p className="text-muted-foreground mb-6">
          The analytics data you're looking for doesn't exist.
        </p>
        <Link
          to="/analytics"
          className="inline-flex items-center space-x-2 bg-primary text-primary-foreground px-6 py-3 rounded-md font-medium hover:bg-primary/90 transition-colors"
        >
          <Icon name="ArrowLeft" size={20} />
          <span>Back to Analytics</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-16">
      <div className="space-y-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate("/analytics")}
              className="p-2 rounded-md hover:bg-muted transition-colors"
            >
              <Icon name="ArrowLeft" size={20} />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                {data.title}
              </h1>
              <p className="text-muted-foreground mt-2">
                Deep dive into your expense analytics
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="last7days">Last 7 Days</option>
              <option value="last30days">Last 30 Days</option>
              <option value="last3months">Last 3 Months</option>
              <option value="lastyear">Last Year</option>
            </select>
            <button className="flex items-center space-x-2 bg-muted text-foreground px-4 py-2 rounded-md font-medium hover:bg-muted/80 transition-colors">
              <Icon name="Download" size={16} />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* Content based on type */}
        {type === "detailed" || !type ? (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-sm text-muted-foreground mb-2">
                  Total Spending
                </h3>
                <p className="text-2xl font-bold text-foreground">
                  {formatCurrency(data.totalSpending)}
                </p>
              </div>
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-sm text-muted-foreground mb-2">
                  Average per Event
                </h3>
                <p className="text-2xl font-bold text-foreground">
                  {formatCurrency(data.averagePerEvent)}
                </p>
              </div>
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-sm text-muted-foreground mb-2">
                  Average per Participant
                </h3>
                <p className="text-2xl font-bold text-foreground">
                  {formatCurrency(data.averagePerParticipant)}
                </p>
              </div>
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-sm text-muted-foreground mb-2">
                  Settlement Rate
                </h3>
                <p className="text-2xl font-bold text-green-60">
                  {data.settlementRate.toFixed(1)}%
                </p>
              </div>
            </div>

            {/* Insights */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Key Insights</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data.insights?.map((insight, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-3 p-4 bg-blue-50 rounded-lg"
                  >
                    <Icon
                      name="Lightbulb"
                      size={20}
                      className="text-blue-600 mt-0.5"
                    />
                    <p className="text-sm text-blue-800">{insight}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : type === "trends" ? (
          <div className="space-y-6">
            {/* Trends Chart Placeholder */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-6">
                Monthly Spending Trends
              </h3>
              <div className="space-y-4">
                {data.yearlyData?.map((month) => (
                  <div
                    key={month.month}
                    className="flex items-center justify-between p-4 bg-muted/20 rounded-lg"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                        <span className="font-medium text-primary">
                          {month.month}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold">
                          {formatCurrency(month.amount)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {month.events} events
                        </p>
                      </div>
                    </div>
                    <div className="w-32 bg-muted rounded-full h-4">
                      <div
                        className="bg-primary h-4 rounded-full"
                        style={{ width: `${(month.amount / 2500) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : type === "categories" ? (
          <div className="space-y-6">
            {/* Category Analysis */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {data.categories?.map((category) => (
                <div
                  key={category.name}
                  className="bg-card border border-border rounded-lg p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: category.color }}
                      />
                      <h3 className="text-lg font-semibold">{category.name}</h3>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        category.trend.startsWith("+")
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {category.trend}
                    </span>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Total Amount
                      </span>
                      <span className="font-semibold">
                        {formatCurrency(category.amount)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Percentage</span>
                      <span className="font-semibold">
                        {category.percentage}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Avg per Event
                      </span>
                      <span className="font-semibold">
                        {formatCurrency(category.avgPerEvent)}
                      </span>
                    </div>

                    <div className="pt-3 border-t border-border">
                      <p className="text-sm text-muted-foreground mb-2">
                        Top Events:
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {category.topEvents?.map((eventName) => (
                          <span
                            key={eventName}
                            className="px-2 py-1 bg-muted text-xs rounded"
                          >
                            {eventName}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : type === "activity" ? (
          <div className="space-y-6">
            {/* Activity Timeline */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-6">Activity Timeline</h3>
              <div className="space-y-4">
                {data.activities?.map((activity, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-4 p-4 hover:bg-muted/20 rounded-lg transition-colors"
                  >
                    <div
                      className={`p-2 rounded-full ${getActivityColor(
                        activity.type
                      )}`}
                    >
                      <Icon name={getActivityIcon(activity.type)} size={16} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-medium">
                          {activity.user} {activity.type.replace("_", " ")}
                          {activity.amount > 0 && (
                            <span className="font-semibold text-green-600 ml-1">
                              {formatCurrency(activity.amount)}
                            </span>
                          )}
                        </p>
                        <span className="text-xs text-muted-foreground">
                          {activity.time}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {activity.event}
                        {activity.category && (
                          <span className="ml-2 px-2 py-0.5 bg-muted text-xs rounded">
                            {activity.category}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AnalyticsDetails;
